// Credit note detail: one refund/credit against a source invoice, with its
// lines and lifecycle actions. Draft notes can be marked sent or voided;
// sent notes are applied (posts the AR adjustment, flips to Applied).
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import {
  applyCreditNote,
  type CreditNoteDetail,
  getCreditNote,
  transitionCreditNote,
} from "@/lib/api";
import { useAccountLabels, useInvoiceNumbers } from "@/lib/labels";

function statusTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case "applied":
      return "success";
    case "sent":
      return "info";
    case "void":
      return "danger";
    default:
      return "idle";
  }
}

function plainStatus(status: string): string {
  const lower = status.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

type TransitionAction = "sent" | "void" | "apply";

// Confirmation copy for the lifecycle actions: apply posts journals and is
// final, void is final, mark-sent only changes status (no email is sent).
const CONFIRMATIONS: Record<
  TransitionAction,
  { title: string; description: string; confirmLabel: string; label: string }
> = {
  sent: {
    title: "Mark this credit note as sent?",
    description:
      "Marks the note sent so it can then be applied to its invoice. It does not email the customer.",
    confirmLabel: "Mark sent",
    label: "Mark sent",
  },
  void: {
    title: "Void this credit note?",
    description:
      "Voiding is final — this note will never post, and it can no longer be marked sent or applied.",
    confirmLabel: "Void credit note",
    label: "Void",
  },
  apply: {
    title: "Apply this credit note to its invoice?",
    description:
      "Applying posts the AR adjustment journals and reduces what the invoice owes. Applied is final — a second apply is rejected rather than double-posted.",
    confirmLabel: "Apply to invoice",
    label: "Apply",
  },
};

export function CreditNoteDetailPage() {
  const { creditNoteId } = useParams({ strict: false }) as {
    creditNoteId: string;
  };
  const [detail, setDetail] = useState<CreditNoteDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{
    message: string;
    journalEntryId?: string;
  } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<TransitionAction | null>(null);
  const accountLabels = useAccountLabels();
  const invoiceNumbers = useInvoiceNumbers();

  const reload = useCallback(() => {
    if (!creditNoteId) return;
    setError(null);
    getCreditNote(creditNoteId)
      .then(setDetail)
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
  }, [creditNoteId]);

  useEffect(reload, [reload]);

  const run = async (action: "sent" | "void" | "apply", label: string) => {
    if (!creditNoteId) return;
    setBusy(action);
    setError(null);
    setNotice(null);
    try {
      if (action === "apply") {
        const result = await applyCreditNote(creditNoteId);
        setNotice({
          message:
            "Applied — the invoice balance was reduced. The adjustment is posted. This is not a cash refund.",
          journalEntryId: result.journal_entry_id || undefined,
        });
      } else {
        await transitionCreditNote(creditNoteId, action);
        setNotice({
          message:
            action === "sent"
              ? "Marked sent — the note can now be applied to its invoice."
              : "Voided — this note will never post.",
        });
      }
      reload();
    } catch (cause) {
      setError(`${label} failed: ${toErrorMessage(cause)}`);
    } finally {
      setBusy(null);
    }
  };

  const confirm = () => {
    if (!confirming) return;
    const action = confirming;
    setConfirming(null);
    void run(action, CONFIRMATIONS[action].label);
  };

  const status = detail?.status.toLowerCase() ?? "";

  return (
    <div className="grid gap-6">
      <nav aria-label="Breadcrumb" className="text-sm">
        <Link
          to="/invoices"
          className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:underline"
        >
          Invoices
        </Link>
        <span
          aria-hidden="true"
          className="mx-2 text-[var(--muted-foreground)]"
        >
          /
        </span>
        <span aria-current="page" className="font-medium">
          {detail?.credit_note_number ?? creditNoteId}
        </span>
      </nav>

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={reload}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {notice ? (
        <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
          {notice.message}
          {notice.journalEntryId ? (
            <>
              {" "}
              <Link
                to="/journal/$entryId"
                params={{ entryId: notice.journalEntryId }}
                className="underline underline-offset-4"
              >
                View journal
              </Link>
            </>
          ) : null}
        </output>
      ) : null}

      {detail === null && !error ? (
        <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
      ) : detail ? (
        <>
          <PageHeader
            title={detail.credit_note_number}
            description={
              <>
                {detail.customer_name} · issued{" "}
                {new Date(detail.issue_date).toLocaleDateString()} ·{" "}
                <StatusDot
                  tone={statusTone(detail.status)}
                  label={plainStatus(detail.status)}
                />
              </>
            }
            actions={
              <>
                {status === "draft" ? (
                  <>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setConfirming("sent")}
                      disabled={busy !== null}
                    >
                      {busy === "sent" ? "Marking…" : "Mark sent"}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setConfirming("void")}
                      disabled={busy !== null}
                    >
                      {busy === "void" ? "Voiding…" : "Void"}
                    </Button>
                  </>
                ) : null}
                {status === "sent" ? (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setConfirming("apply")}
                      disabled={busy !== null}
                      title="Post the adjustment and reduce the invoice balance (Applied is final — a second apply is rejected)"
                    >
                      {busy === "apply" ? "Applying…" : "Apply to invoice"}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setConfirming("void")}
                      disabled={busy !== null}
                    >
                      {busy === "void" ? "Voiding…" : "Void"}
                    </Button>
                  </>
                ) : null}
              </>
            }
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Total
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {formatMoney({ cents: detail.total_minor, currency: "USD" })}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Source invoice
              </p>
              <p className="mt-1 text-sm">
                {detail.related_invoice_id ? (
                  <Link
                    to="/invoices/$invoiceId"
                    params={{ invoiceId: detail.related_invoice_id }}
                    className="text-xs text-[var(--action)] underline-offset-4 hover:underline"
                  >
                    {invoiceNumbers.get(detail.related_invoice_id) ?? "Invoice"}
                  </Link>
                ) : (
                  <span className="text-[var(--muted-foreground)]">
                    None linked
                  </span>
                )}
              </p>
              <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                Applying is only possible with a linked invoice, and the total
                must fit the invoice's remaining balance.
              </p>
            </div>
          </div>

          <div className="surface overflow-hidden rounded-2xl">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>What</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detail.lines.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3}>
                      This credit note has no lines.
                    </TableCell>
                  </TableRow>
                ) : (
                  detail.lines.map((line) => (
                    <TableRow key={line.id}>
                      <TableCell>{line.description}</TableCell>
                      <TableCell className="text-xs">
                        {accountLabels.has(line.account_id) ? (
                          accountLabels.get(line.account_id)
                        ) : (
                          <span
                            className="text-[var(--muted-foreground)]"
                            title={line.account_id}
                          >
                            Unknown account
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney({
                          cents: line.amount_minor,
                          currency: "USD",
                        })}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </>
      ) : null}

      <Dialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
        title={confirming ? CONFIRMATIONS[confirming].title : ""}
        description={
          confirming ? CONFIRMATIONS[confirming].description : undefined
        }
      >
        {confirming ? (
          <div className="actions-row">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setConfirming(null)}
            >
              Cancel
            </Button>
            <Button type="button" onClick={confirm}>
              {CONFIRMATIONS[confirming].confirmLabel}
            </Button>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}
